import { useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Upload,
  FileText,
  CheckCircle,
  Loader2,
  AlertCircle,
  User,
  Mail,
  Phone,
  Linkedin,
  Globe,
} from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'

interface CustomQuestion {
  id: string
  type: 'text' | 'textarea' | 'select' | 'radio' | 'checkbox'
  question: string
  required: boolean
  options?: { label: string; value: string }[]
}

interface ApplicationFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  jobId: string
  jobTitle: string
  companyName: string
  tenantSlug: string
  customQuestions?: CustomQuestion[]
  onSuccess?: () => void
}

interface FormData {
  firstName: string
  lastName: string
  email: string
  phone?: string
  linkedinUrl?: string
  portfolioUrl?: string
  coverLetter?: string
  answers?: Record<string, string | string[]>
}

export function ApplicationForm({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  companyName,
  tenantSlug,
  customQuestions = [],
  onSuccess,
}: ApplicationFormProps) {
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [step, setStep] = useState(1)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      linkedinUrl: '',
      portfolioUrl: '',
      coverLetter: '',
      answers: {},
    },
  })

  const submitApplication = useMutation({
    mutationFn: async (data: FormData) => {
      // Create form data for file upload
      const formData = new FormData()
      formData.append('jobId', jobId)
      formData.append('firstName', data.firstName)
      formData.append('lastName', data.lastName)
      formData.append('email', data.email)
      if (data.phone) formData.append('phone', data.phone)
      if (data.linkedinUrl) formData.append('linkedinUrl', data.linkedinUrl)
      if (data.portfolioUrl) formData.append('portfolioUrl', data.portfolioUrl)
      if (data.coverLetter) formData.append('coverLetter', data.coverLetter)
      if (data.answers) formData.append('answers', JSON.stringify(data.answers))
      if (resumeFile) formData.append('resume', resumeFile)

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90))
      }, 200)

      try {
        const response = await fetch(`/api/v1/public/${tenantSlug}/apply`, {
          method: 'POST',
          body: formData,
        })

        clearInterval(progressInterval)
        setUploadProgress(100)

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.message || 'Failed to submit application')
        }

        return response.json()
      } catch (error) {
        clearInterval(progressInterval)
        throw error
      }
    },
    onSuccess: () => {
      toast({ title: 'Application submitted successfully!', variant: 'success' })
      onOpenChange(false)
      onSuccess?.()
    },
    onError: (error) => {
      setUploadProgress(0)
      toast({
        title: 'Failed to submit application',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      })
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ]
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF or Word document',
          variant: 'destructive',
        })
        return
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 5MB',
          variant: 'destructive',
        })
        return
      }

      setResumeFile(file)
    }
  }

  const removeFile = () => {
    setResumeFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const onSubmit = (data: FormData) => {
    submitApplication.mutate(data)
  }

  const totalSteps = customQuestions.length > 0 ? 3 : 2

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Apply for {jobTitle}</DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center gap-2 mb-6">
          {[...Array(totalSteps)].map((_, i) => (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-full transition-colors ${
                i + 1 <= step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-medium">Personal Information</h3>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="firstName"
                        {...register('firstName', { required: 'First name is required' })}
                        placeholder="John"
                        className="pl-9"
                      />
                    </div>
                    {errors.firstName && (
                      <p className="text-sm text-destructive">{errors.firstName.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      {...register('lastName', { required: 'Last name is required' })}
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="text-sm text-destructive">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      placeholder="john@example.com"
                      className="pl-9"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      {...register('phone')}
                      placeholder="+1 (555) 000-0000"
                      className="pl-9"
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="linkedinUrl">LinkedIn (optional)</Label>
                    <div className="relative">
                      <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="linkedinUrl"
                        {...register('linkedinUrl')}
                        placeholder="linkedin.com/in/..."
                        className="pl-9"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="portfolioUrl">Portfolio (optional)</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="portfolioUrl"
                        {...register('portfolioUrl')}
                        placeholder="yourwebsite.com"
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Resume & Cover Letter */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-medium">Resume & Cover Letter</h3>

                {/* Resume Upload */}
                <div className="space-y-2">
                  <Label>Resume *</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {resumeFile ? (
                    <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                      <FileText className="h-8 w-8 text-primary" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{resumeFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(resumeFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={removeFile}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-8 border-2 border-dashed rounded-lg hover:border-primary/50 hover:bg-muted/50 transition-colors"
                    >
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="font-medium">Click to upload your resume</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        PDF or Word document (max 5MB)
                      </p>
                    </button>
                  )}
                </div>

                {/* Cover Letter */}
                <div className="space-y-2">
                  <Label htmlFor="coverLetter">Cover Letter (optional)</Label>
                  <Textarea
                    id="coverLetter"
                    {...register('coverLetter')}
                    placeholder={`Why are you interested in joining ${companyName}?`}
                    rows={5}
                  />
                </div>
              </motion.div>
            )}

            {/* Step 3: Custom Questions */}
            {step === 3 && customQuestions.length > 0 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-medium">Additional Questions</h3>

                {customQuestions.map((q) => (
                  <div key={q.id} className="space-y-2">
                    <Label>
                      {q.question}
                      {q.required && ' *'}
                    </Label>

                    {q.type === 'text' && (
                      <Input
                        {...register(`answers.${q.id}`, {
                          required: q.required ? 'This field is required' : false,
                        })}
                      />
                    )}

                    {q.type === 'textarea' && (
                      <Textarea
                        {...register(`answers.${q.id}`, {
                          required: q.required ? 'This field is required' : false,
                        })}
                        rows={3}
                      />
                    )}

                    {q.type === 'select' && (
                      <select
                        {...register(`answers.${q.id}`, {
                          required: q.required ? 'This field is required' : false,
                        })}
                        className="w-full h-10 px-3 rounded-md border bg-background"
                      >
                        <option value="">Select an option</option>
                        {q.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {q.type === 'radio' && (
                      <div className="space-y-2">
                        {q.options?.map((opt) => (
                          <label
                            key={opt.value}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              value={opt.value}
                              {...register(`answers.${q.id}`, {
                                required: q.required ? 'This field is required' : false,
                              })}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {q.type === 'checkbox' && (
                      <div className="space-y-2">
                        {q.options?.map((opt) => (
                          <label
                            key={opt.value}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              value={opt.value}
                              {...register(`answers.${q.id}`)}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Upload Progress */}
          {submitApplication.isPending && uploadProgress > 0 && (
            <div className="space-y-2">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  className="h-full bg-primary"
                />
              </div>
              <p className="text-sm text-center text-muted-foreground">
                Submitting application...
              </p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between gap-4 pt-4 border-t">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(step - 1)}
                disabled={submitApplication.isPending}
              >
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < totalSteps ? (
              <Button
                type="button"
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 2 && !resumeFile)
                }
              >
                Continue
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={submitApplication.isPending || !resumeFile}
              >
                {submitApplication.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
